/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable indent */
import './style.scss';

import { useMemo, useRef, useState } from 'react';
import { useFieldArray, useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Tooltip } from 'primereact/tooltip';
import ReactRouterPrompt from 'react-router-prompt';

import {
  create as createCouncil,
  getAllCouncilMemberRoles,
  getAllCouncilTypes,
} from 'apis/councils.api';
import { getAll as getAllOrganizations } from 'apis/organizations.api';
import { generalInfoForCouncil, getAllVersions } from 'apis/projectProposals.api';
import { Can } from 'components/Can';
import ErrorDialog from 'components/ErrorDialog';
import { DatePicker, FileInput, TextInput } from 'components/FormControl';
import Loading from 'components/Loading';
import { COUNCIL_TYPE_CODE, PROJECT_ROLES, TEMPLATE, TOAST } from 'constant';
import MemberDialog from 'features/Council/components/AddMemberDialog';
import ConfirmSendMailDialog from 'features/Council/components/ConfirmSendMailDialog';
import { createCouncilValidationSchema } from 'features/Council/validation';
import { toast } from 'layout';
import {
  getAcadamicLevelShortHand,
  getCodeCouncilTypeById,
  getContactOrganizationName,
  getEndingTimeOfDate,
  getNameCouncilType,
  getNameRoleOfMemberInCouncil,
  getOrganizationNameById,
} from 'utils/func';
import DateTimePicker from 'components/FormControl/DateTimePicker';
import ConfirmCloseCouncilDialog from 'features/Council/components/ConfirmCloseCouncilDialog';

const formOptions = { resolver: yupResolver(createCouncilValidationSchema) };
export default function CreateCouncil() {
  // #region Data
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isVisibleErrorDialog, setIsVisibleDialogError] = useState(false);
  const [isVisibleConfirmDoneDialog, setIsVisibleConfirmDoneDialog] = useState(false);
  const [isSentMail, setIsSentMail] = useState(false);
  const [isCloseCouncil, setIsCloseCouncil] = useState([false, true]);
  const { councilTypeId, projectProposalId, summaryReportId } = useParams();

  const { data: councilMemberRolesData } = useQuery(
    ['councilMemberRoles'],
    getAllCouncilMemberRoles,
    {
      staleTime: 5000 * 60,
    }
  );
  const roles = useMemo(() => {
    if (councilMemberRolesData?.data?.length > 0) {
      return councilMemberRolesData.data.map((councilMemberRole) => ({
        value: councilMemberRole.id,
        name: t(`council.role.${councilMemberRole.code}`),
      }));
    }
    return [];
  }, [councilMemberRolesData?.data, t]);

  const { data: councilTypesData } = useQuery(['councilTypes'], getAllCouncilTypes, {
    staleTime: 5000 * 60,
  });
  const typeCouncils = useMemo(() => {
    if (councilTypesData?.data?.length > 0) {
      return councilTypesData.data.map((councilType) => ({
        value: councilType.id,
        name: `${t('council.viewList.create')} ${t(`council.viewList.${councilType.code}`)}`,
        code: councilType.code,
        id: councilType.id, // use check council type
      }));
    }
    return [];
  }, [councilTypesData?.data, t]);

  const { data: rawOrganizations } = useQuery(['Organizations'], getAllOrganizations, {
    staleTime: 1000 * 60 * 5,
  });
  const organizations = useMemo(
    () =>
      rawOrganizations?.data.map((e) => ({
        ...e,
        name: t('lang') === 'vi' ? e?.name : e?.englishName,
      })) ?? [],
    [rawOrganizations, t]
  );

  const { data: allProjectProposalVersionsData } = useQuery(
    ['allProjectProposalVersions', projectProposalId],
    () => getAllVersions(projectProposalId)
  );
  const [previousVersion, latestVersion] = useMemo(() => {
    if (allProjectProposalVersionsData?.data?.length > 0) {
      const currentCouncilType = councilTypesData?.data?.find((item) => item?.id === councilTypeId);
      // dành cho hội đồng giữa kỳ và nghiệm thu
      if (
        [
          COUNCIL_TYPE_CODE.MIDTERM_REPORT_COUNCIL,
          COUNCIL_TYPE_CODE.ACCEPTANCE_COUNCIL,
          COUNCIL_TYPE_CODE.LIQUIDATION_COUNCIL,
        ].includes(currentCouncilType.code)
      ) {
        // đầy đủ hội đồng tới tài chính
        if (allProjectProposalVersionsData?.data?.length === 4) {
          return [
            allProjectProposalVersionsData?.data?.find((item) => item?.version === 4),
            allProjectProposalVersionsData?.data?.find((item) => item?.version === 4),
          ];
        }
        // đề tài không có các hội đồng từ sơ tuyển - tài chính, mà tạo thẳng giữa kỳ
        if (allProjectProposalVersionsData?.data?.length === 1) {
          return [
            allProjectProposalVersionsData?.data?.find((item) => item?.version === 1),
            allProjectProposalVersionsData?.data?.find((item) => item?.version === 1),
          ];
        }
      }
      // dành cho các hội đồng từ sơ tuyển - tài chính
      return [
        allProjectProposalVersionsData?.data?.find(
          (item) => item?.version === currentCouncilType?.order
        ),
        allProjectProposalVersionsData?.data?.reduce((a, b) => (a?.version > b?.version ? a : b)),
      ];
    }
    return [null, null];
  }, [allProjectProposalVersionsData, councilTypesData]);

  // eslint-disable-next-line no-unused-vars
  const isCombineSpecializationAndFinance = useMemo(
    () => previousVersion?.template === TEMPLATE[1595],
    [previousVersion]
  );

  const { data: generalInfoProjectProposalData, isLoading: isLoadingData } = useQuery(
    ['GeneralInfoProjectProposal', previousVersion?.id],
    () => generalInfoForCouncil(previousVersion?.id),
    {
      staleTime: 1000 * 60 * 5,
      enabled: !!previousVersion?.id,
    }
  );
  const generalInfoProjectProposal = useMemo(
    () => generalInfoProjectProposalData?.data ?? {},
    [generalInfoProjectProposalData]
  );
  const owner = useMemo(
    () =>
      generalInfoProjectProposal?.projectProposalContacts?.find(
        (item) => item?.projectRole?.code === PROJECT_ROLES.OWNER
      ) ?? {},
    [generalInfoProjectProposal]
  );

  // LƯU Ý: Đề tài chưa clone phiên bản thầy Vũ bảo vẫn hiện thông tin đề tài
  // => Hiển thị thông tin phiên bản mới nhất
  const { data: generalInfoLatestVersionData } = useQuery(
    ['GeneralInfoLatestVersion', latestVersion?.id],
    () => generalInfoForCouncil(latestVersion?.id),
    {
      enabled: !!latestVersion?.id,
    }
  );
  const generalInfoLatestVersion = useMemo(
    () => generalInfoLatestVersionData?.data,
    [generalInfoLatestVersionData]
  );
  const ownerLatestVersion = useMemo(
    () =>
      generalInfoLatestVersion?.projectProposalContacts?.find(
        (item) => item?.projectRole?.code === PROJECT_ROLES.OWNER
      ) ?? {},
    [generalInfoLatestVersion]
  );

  const [council, setCouncil] = useState(null);
  const MemberRef = useRef();
  const {
    mutate,
    error: errorRes,
    isError: isErrorRes,
    isLoading: isLoadingCreate,
  } = useMutation(createCouncil);
  const formDefaultValues = {
    name: '',
    establishDate: '',
    membersFields: [],
  };
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isValid },
  } = useForm({ mode: 'onChange', defaultValues: formDefaultValues, ...formOptions });

  const {
    fields: membersFields,
    append: appendMembers,
    remove: removeMembers,
    update: updateMembers,
  } = useFieldArray({
    control,
    name: 'councilMembers',
  });

  const { isDirty, dirtyFields } = useFormState({
    control,
  });

  const getPageMargins = () => '@page { margin: 0.5in 0.5in 0.2in 0.2in  !important; }';
  // #endregion

  // #region Events
  const onSubmit = async (data) => {
    const formData = new FormData();

    if (membersFields.length === 0) {
      toast(TOAST.ERROR, t('errorMessage.councilMustHaveAtLeastOneMember'));
      return;
    }

    if (data?.decisionToEstablish) {
      formData.append('establishmentFile', data.decisionToEstablish);
    }
    if (data?.establishDate) {
      formData.append('timeCreated', getEndingTimeOfDate(new Date(data.establishDate)));
    }
    if (data?.reviewDeadline) {
      formData.append('reviewDeadline', getEndingTimeOfDate(new Date(data.reviewDeadline)));
    }
    if (data?.timeMet) {
      formData.append('timeMet', new Date(data.timeMet));
    }
    if (data?.meetingLocation) {
      formData.append('meetingLocation', data.meetingLocation);
    }
    if (data?.contact) {
      formData.append('contact', data.contact);
    }
    if (data?.documentUrl) {
      formData.append('documentUrl', data.documentUrl);
    }
    formData.append('name', data.name);
    formData.append('members', JSON.stringify(membersFields));
    formData.append('projectProposalId', previousVersion?.id);
    formData.append('councilTypeId', councilTypeId);
    if (summaryReportId) {
      formData.append('summaryReportId', summaryReportId);
    }
    mutate(formData, {
      onSuccess: (res) => {
        reset({}, { keepValues: true });
        toast(TOAST.SUCCESS, t('council.create.success'));
        setCouncil(res?.data);
      },
      onError: () => {
        setIsVisibleDialogError(true);
      },
    });
  };

  const footerConfirmationPrompt = (onConfirm, onCancel) => (
    <div>
      <Button
        label={t('formLayout.action.cancel')}
        className="w-7rem"
        severity="danger"
        onClick={onCancel}
        autoFocus
      />
      <Button
        label={t('formLayout.action.ok')}
        className="w-7rem"
        severity="info"
        onClick={onConfirm}
      />
    </div>
  );

  const checkValidation = () => {
    if (!isValid) {
      toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
    }
  };

  const handleCloseCouncil = () => {
    if (council && !isSentMail) {
      setIsCloseCouncil([true, false]);
    } else {
      navigate(-1);
    }
  };
  // #endregion

  return (
    <>
      {(isLoadingData || isLoadingCreate) && <Loading />}
      {isErrorRes && (
        <ErrorDialog
          title={t(
            `council.create.titleCreateError.${getCodeCouncilTypeById(typeCouncils, councilTypeId)}`
          )}
          isError={isErrorRes}
          error={errorRes}
          visible={isVisibleErrorDialog}
          setVisible={setIsVisibleDialogError}
        />
      )}
      <ConfirmSendMailDialog
        visible={isVisibleConfirmDoneDialog}
        setVisible={setIsVisibleConfirmDoneDialog}
        setIsSentMail={setIsSentMail}
        council={council}
      />
      <ReactRouterPrompt
        when={
          (isDirty && Object.keys(dirtyFields).length) ||
          (council && !isSentMail && isCloseCouncil[1])
        }
      >
        {({ isActive, onConfirm, onCancel }) => (
          <>
            {council && !isSentMail ? (
              <ConfirmCloseCouncilDialog
                visible={isActive}
                setVisible={setIsCloseCouncil}
                onConfirm={onConfirm}
                onHide={onCancel}
              />
            ) : (
              <Dialog
                visible={isActive}
                header={<p className="text-blue-500">{t('projectProposal.confirmationTitle')}</p>}
                position="center"
                style={{ width: '300px' }}
                onHide={onCancel}
                draggable={false}
                footer={() => footerConfirmationPrompt(onConfirm, onCancel)}
              >
                <p className="text-lg py-3">{t('projectProposal.confirmationPromptMessage')}</p>
              </Dialog>
            )}
          </>
        )}
      </ReactRouterPrompt>
      <div className="card relative detail-info-project-proposal">
        <style>{getPageMargins()}</style>
        <div className="flex">
          <h3 className="mb-5 line-height-3 text-4xl text-center font-bold flex-1">
            {getNameCouncilType(typeCouncils, councilTypeId)}{' '}
            {isCombineSpecializationAndFinance ? t('council.viewList.andFinance') : ''}
          </h3>
        </div>

        <h4>{t('projectProposal.general.viewLabelCouncil')}</h4>

        <div className="ml-5 text-lg">
          <div className="flex mb-1">
            <div className="mr-2 font-semibold">
              {t('projectProposal.general.projectNameAndType.vietnameseName')}:
            </div>
            <div className="flex-1">
              {generalInfoProjectProposal?.vietnameseName ||
                generalInfoLatestVersion?.vietnameseName}
            </div>
          </div>
          <div className="flex mb-1">
            <div className="mr-2 font-semibold">
              {t('projectProposal.general.projectNameAndType.englishName')}:{' '}
            </div>
            <div className="flex-1">
              {generalInfoProjectProposal?.englishName || generalInfoLatestVersion?.englishName}
            </div>
          </div>
          <div className="flex mb-1">
            <div className="mr-2 font-semibold">
              {t('projectProposal.general.ownerSection.labelCouncil')}:{' '}
            </div>
            <div className="flex-1">
              {owner?.fullname || generalInfoProjectProposal?.owner?.fullname
                ? `${getAcadamicLevelShortHand(
                    owner?.academicRank,
                    owner?.academicDegree,
                    t,
                    true
                  )}${owner?.fullname ?? generalInfoProjectProposal?.owner?.fullname ?? ''}`
                : `${getAcadamicLevelShortHand(
                    ownerLatestVersion?.academicRank,
                    ownerLatestVersion?.academicDegree,
                    t,
                    true
                  )}${
                    ownerLatestVersion?.fullname ?? generalInfoLatestVersion?.owner?.fullname ?? ''
                  }`}
            </div>
          </div>
        </div>

        <h4 className="mt-4">
          {t(
            `council.create.label.${getCodeCouncilTypeById(typeCouncils, councilTypeId)}${
              isCombineSpecializationAndFinance ? 'AndFinance' : ''
            }`
          )}
        </h4>
        <form className="p-fluid ml-5" onSubmit={handleSubmit(onSubmit)}>
          <div className="relative">
            <div>
              <TextInput
                label={t(
                  `council.fields.name.${getCodeCouncilTypeById(typeCouncils, councilTypeId)}`
                )}
                isRequired
                name="name"
                isFontWeightLabel
                control={control}
                errors={errors}
                disabled={!!council}
              />
            </div>
            <div className="flex flex-wrap ">
              <div className="w-3">
                <DatePicker
                  label={t('council.fields.establishDate')}
                  isRequired
                  name="establishDate"
                  isFontWeightLabel
                  control={control}
                  errors={errors}
                  disabled={!!council}
                />
              </div>
              <div className="w-3 ml-8">
                <DatePicker
                  label={t('council.fields.reviewDeadline')}
                  name="reviewDeadline"
                  isRequired
                  isFontWeightLabel
                  control={control}
                  errors={errors}
                  disabled={!!council}
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-6">
              <div className="w-3">
                <DateTimePicker
                  label={t('council.createForManyProjectProposal.infoCouncil.timeMet')}
                  name="timeMet"
                  isFontWeightLabel
                  control={control}
                  errors={errors}
                  disabled={!!council}
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-6">
              <div className="w-12">
                <TextInput
                  label={t('council.createForManyProjectProposal.infoCouncil.meetingLocation')}
                  name="meetingLocation"
                  isFontWeightLabel
                  control={control}
                  errors={errors}
                  disabled={!!council}
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-6">
              <div className="w-12">
                <TextInput
                  label={t('council.createForManyProjectProposal.infoCouncil.contact')}
                  name="contact"
                  isFontWeightLabel
                  control={control}
                  errors={errors}
                  disabled={!!council}
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-6">
              <div className="w-12">
                <TextInput
                  label={t('council.createForManyProjectProposal.infoCouncil.documentUrl')}
                  name="documentUrl"
                  isFontWeightLabel
                  control={control}
                  errors={errors}
                  disabled={!!council}
                />
              </div>
            </div>

            <div>
              <FileInput
                label={t('council.fields.decisionToEstablish')}
                isFontWeightLabel
                name="decisionToEstablish"
                accept=".pdf"
                acceptOnly="pdfOnly"
                isRequired
                control={control}
                errors={errors}
                disabled={!!council}
              />
            </div>
          </div>

          <div className="mt-5 mb-3" style={{ fontWeight: 900, fontSize: '20px' }}>
            {t('council.create.titleContent')}
          </div>
          <div className="form-table">
            <table className="mb-2">
              <thead>
                <tr>
                  <th className="text-center" style={{ minWidth: 50, width: 50 }}>
                    {t('formLayout.numericalOrder')}
                  </th>
                  <th className="text-center" style={{ minWidth: 200, width: '30%' }}>
                    {`${t('council.member.fields.academicRank')}, ${t(
                      'council.member.fields.academicDegree'
                    )}, ${t('council.member.fields.name')}`}
                  </th>
                  <th className="text-center" style={{ minWidth: 200, width: '33%' }}>
                    Email
                  </th>
                  <th className="text-center" style={{ minWidth: 200, width: '33%' }}>
                    {t('council.member.fields.organization')}
                  </th>
                  <th className="text-center" style={{ minWidth: 200, width: '33%' }}>
                    {t('council.member.fields.role')}
                  </th>

                  {!council && (
                    <th className="float text-center" style={{ minWidth: 80, width: 80 }}>
                      {t('council.member.fields.action')}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                <tr className="font-bold">
                  {!council && (
                    <td colSpan={6}>
                      <div>
                        <span>{t('council.create.add')}</span>
                        <i
                          onClick={() => {
                            MemberRef.current.open({
                              append: appendMembers,
                              type: 'APPEND',
                            });
                          }}
                          className="add-scientific-secretary pi pi-plus text-primary cursor-pointer bg-primary p-1 border-circle ml-1"
                          data-pr-tooltip={t('formLayout.action.add')}
                          data-pr-position="left"
                        />
                        <Tooltip target=".add-scientific-secretary" />
                      </div>
                    </td>
                  )}
                </tr>
                {membersFields.map((item, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={index}>
                    <td className="text-center">{index + 1}</td>
                    <td>
                      {`${getAcadamicLevelShortHand(
                        item?.academicRank,
                        item?.academicDegree,
                        t,
                        true
                      )}${item?.fullname}`}
                    </td>
                    <td>{item?.email}</td>
                    <td>
                      {typeof value === 'object'
                        ? getContactOrganizationName(organizations, item?.contact, t)
                        : getOrganizationNameById(
                            item?.organizationId,
                            organizations,
                            item?.specificOrganizationName,
                            t
                          )}
                    </td>
                    <td>{getNameRoleOfMemberInCouncil(roles, item?.roleId)}</td>
                    {!council && (
                      <td className="flex flex-column float text-center p-0 border-none border-bottom-1">
                        <i
                          onClick={() => {
                            MemberRef.current.open({
                              update: updateMembers,
                              index,
                              value: item,
                              type: 'UPDATE',
                            });
                          }}
                          className="edit-icon pi pi-pencil bg-red text-primary cursor-pointer my-2"
                          data-pr-tooltip={t('formLayout.action.edit')}
                          data-pr-position="left"
                        />
                        <Tooltip target=".edit-icon" />

                        <i
                          onClick={() => removeMembers(index)}
                          className="remove-icon pi pi-times text-red-700 cursor-pointer mb-2"
                          data-pr-tooltip={t('formLayout.action.remove')}
                          data-pr-position="left"
                        />
                        <Tooltip target=".remove-icon" />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap justify-content-end mt-8">
            <Button
              label={t('formLayout.action.close')}
              severity="danger"
              type="button"
              onClick={handleCloseCouncil}
              className="mr-2 w-8rem"
            />
            <Button
              label={t('formLayout.action.save')}
              severity="info"
              type="submit"
              onClick={checkValidation}
              disabled={!(isDirty && Object.keys(dirtyFields).length)}
              className="mr-2 w-8rem"
            />
            <Can do="send_mail" on="council">
              <Button
                label={t('formLayout.action.doneAndSendMail')}
                severity="success"
                type="button"
                onClick={() => setIsVisibleConfirmDoneDialog(true)}
                disabled={(!council && !isSentMail) || (council && isSentMail)}
                className="mr-2 w-auto"
              />
            </Can>
          </div>
          <MemberDialog
            ref={MemberRef}
            organizations={organizations}
            curMembers={membersFields}
            roles={roles}
            projectProposalMembers={generalInfoProjectProposal?.projectProposalContacts}
          />
          <ConfirmCloseCouncilDialog visible={isCloseCouncil[0]} setVisible={setIsCloseCouncil} />
        </form>
      </div>
    </>
  );
}
